import { useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for permission checking
 * Provides easy access to permission checking functions
 */
export const usePermissions = () => {
  const { 
    user, 
    userPermissions, 
    userRoles, 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions,
    canAccessOwnData,
    ROLES 
  } = useAuth();
  
  /**
   * Check if user has a specific permission
   * @param {string} permission - The permission to check
   * @returns {boolean} - True if user has the permission
   */
  const hasPermissionCheck = useCallback(
    (permission) => {
      if (!user || !userPermissions) return false;
      return hasPermission(permission);
    },
    [user, userPermissions, hasPermission]
  );
  
  /**
   * Check if user has any of the required permissions
   * @param {string[]} permissionList - Array of permissions to check
   * @returns {boolean} - True if user has any of the permissions
   */
  const hasAnyPermissionCheck = useCallback(
    (permissionList) => {
      if (!user || !userPermissions) return false;
      return hasAnyPermission(permissionList);
    },
    [user, userPermissions, hasAnyPermission]
  );
  
  /**
   * Check if user has all of the required permissions
   * @param {string[]} permissionList - Array of permissions to check
   * @returns {boolean} - True if user has all of the permissions
   */
  const hasAllPermissionsCheck = useCallback(
    (permissionList) => {
      if (!user || !userPermissions) return false;
      return hasAllPermissions(permissionList);
    },
    [user, userPermissions, hasAllPermissions]
  );

  /**
   * Check if user has a specific role
   * @param {string} role - The role to check
   * @returns {boolean} - True if user has the role
   */
  const hasRoleCheck = useCallback(
    (role) => {
      if (!user || !userRoles) return false;
      return userRoles.includes(role);
    },
    [user, userRoles]
  );

  /**
   * Check if user has any of the required roles
   * @param {string[]} roleList - Array of roles to check
   * @returns {boolean} - True if user has any of the roles
   */
  const hasAnyRole = useCallback(
    (roleList) => {
      if (!user || !userRoles) return false;
      return roleList.some(role => userRoles.includes(role));
    },
    [user, userRoles]
  );

  /**
   * Check if user is super admin
   * @returns {boolean} - True if user is super admin
   */
  const isSuperAdmin = useCallback(() => {
    return hasRoleCheck(ROLES.SUPER_ADMIN);
  }, [hasRoleCheck, ROLES]);

  const permissionsList = useMemo(() => userPermissions || [], [userPermissions]);
  const rolesList = useMemo(() => userRoles || [], [userRoles]);

  return {
    // Permission checking functions
    hasPermission: hasPermissionCheck,
    hasAnyPermission: hasAnyPermissionCheck,
    hasAllPermissions: hasAllPermissionsCheck,
    
    // Role checking functions
    hasRole: hasRoleCheck,
    hasAnyRole,
    isSuperAdmin,
    
    // Data access functions
    canAccessOwnData,
    permissions: permissionsList,
    roles: rolesList,
    user,
    
    // Constants
    ROLES,
  };
};

export default usePermissions;
